import './Customers.scss';
import { Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import Backend from '../backend/Backend';
import { useOktaAuth } from '@okta/okta-react';
import { ITenantData } from '../common/types';
import TenantLogo from './TenantLogo';
import AppRoutes from '../AppRoutes';
import Util, { TenantInfo } from '../Util';
import UserRoleType from '../UserRoleType';
import AddNewCustomerDialog from './AddNewCustomerDialog';
import _ from 'lodash';

interface CustomersProps extends RouteComponentProps {
    progress: Function;
}

interface ISelectProps {
    onChange: Function;
}
const Customers: FunctionComponent<CustomersProps> = (props: CustomersProps) => {
    const { t } = useTranslation();
    const { authState, oktaAuth } = useOktaAuth();
    const history = useHistory();
    const { mode } = useParams<{ mode: string }>();

    const [tenantsList, setTenantsList] = useState<ITenantData[]>([]);
    const [customersFiltered, setCustomersFiltered] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [sortBy, setSortBy] = useState('last_updated');
    const [openNewCustomer, setOpenNewCustomer] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<TenantInfo>(null);
    const colorMap = useRef({});

    const setTenantInfoAndRedirect = async (tenantInfo: ITenantData) => {
        const tenantInfoObj = { id: tenantInfo.rowid, name: tenantInfo.tenant_name, logo: tenantInfo.logo_url };
        Util.setCurrentTenantInfo(tenantInfoObj);
        Util.setSessionCurrentTenantInfo(tenantInfoObj);

        const userDataResponse = await Backend.getUserInfo(oktaAuth);
        if (userDataResponse.data.urole === UserRoleType.external) {
            history.push(AppRoutes.externalHome);
        } else {
            history.push(AppRoutes.configurations);
        }
    };

    const getAllTenants = async () => {
        props.progress(true);
        const result = await Backend.getAllTenants(oktaAuth);
        props.progress(false);

        if (result.data.length === 1) {
            const tenantInfo = result.data[0];
            setTenantInfoAndRedirect(tenantInfo);
        } else if (mode === 'switch') {
            setTenantsList(result.data);
        } else if (Util.getCurrentTenantInfo() && Util.getCurrentTenantInfo().id) {
            history.push(AppRoutes.configurations);
        } else {
            setTenantsList(result.data);
        }

        props.progress(false);
    };

    useEffect(() => {
        getAllTenants();
    }, [oktaAuth]);

    useEffect(() => {
        if (tenantsList.length > 0) {
            let newList = tenantsList;
            if (searchValue) {
                newList = newList.filter((customer) =>
                    customer.tenant_name.toLowerCase().includes(searchValue.toLowerCase()),
                );
            }
            setCustomersFiltered(newList);
        }
    }, [tenantsList, searchValue, sortBy]);

    const getSortedList = (list: ITenantData[]) => {
        if (sortBy === 'last_updated') {
            return list.sort((a, b) => {
                if (!a.last_updated_project) {
                    return 1;
                }
                if (!b.last_updated_project) {
                    return -1;
                }
                return a.last_updated_project < b.last_updated_project
                    ? 1
                    : a.last_updated_project > b.last_updated_project
                      ? -1
                      : 0;
            });
        }
        return list.sort((a, b) => {
            const nameA = a.tenant_name.toUpperCase();
            const nameB = b.tenant_name.toUpperCase();
            if (sortBy === 'asc') {
                return nameA.localeCompare(nameB);
            }
            return nameB.localeCompare(nameA);
        });
    };

    const addNewCustomer = (newCustomer: ITenantData) => {
        const tenantsListCopy = _.cloneDeep(tenantsList);
        tenantsListCopy.push(newCustomer);
        setTenantsList(tenantsListCopy);
        setTenantInfoAndRedirect(newCustomer);
    };

    const SortBySelect = () => (
        <Stack className="SortByDiv">
            <Typography>{t('customers.sort-by.sort-by')}</Typography>
            <select
                className="SortBySelect"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="search-tenant-sortby"
            >
                <option value="last_updated">{t('customers.sort-by.last-updated')}</option>
                <option value="asc">{t('customers.sort-by.az')}</option>
                <option value="desc">{t('customers.sort-by.za')}</option>
            </select>
        </Stack>
    );

    const handleSelection = (id: string, name: string, logo: string) => {
        setSelectedCustomer({ id, name, logo });
    };

    const handleNextClick = () => {
        if (selectedCustomer) {
            Util.setCurrentTenantInfo(selectedCustomer);
            Util.setSessionCurrentTenantInfo(selectedCustomer);
            goToConfigurationsPage();
        }
    };

    const goToConfigurationsPage = () => {
        history.push(AppRoutes.configurations);
    };

    return (
        <Stack className="CustomersContainer" direction="row">
            {tenantsList && tenantsList.length > 0 ? (
                <Stack className="Customers" spacing={2} direction="column">
                    <Typography variant="h6">{t('customers.select-customer')}</Typography>
                    <Stack className="CustomersList">
                        <Stack className="CustomersListHeader">
                            <Typography className="ListTitle">
                                <span>{tenantsList.length}</span> {t('customers.customers')}
                            </Typography>
                            <Button onClick={() => setOpenNewCustomer(true)} className="AddCustomerBtn">
                                {t('customers.add-customer')}
                            </Button>
                        </Stack>
                        <TextField
                            className="SearchCustomers"
                            placeholder={t('common.search-by-keyword')}
                            size="small"
                            onChange={(e) => setSearchValue(e.target.value)}
                            value={searchValue}
                            inputProps={{
                                'data-testid': 'search-tenant-textfield',
                            }}
                            // eslint-disable-next-line react/jsx-no-duplicate-props
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" style={{ paddingRight: '20px' }}>
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <SortBySelect />
                        <div className="CustomersListStack">
                            {customersFiltered
                                ? getSortedList(customersFiltered).map((customer) =>
                                      TenantLogo(
                                          customer,
                                          () =>
                                              handleSelection(customer.rowid, customer.tenant_name, customer.logo_url),
                                          selectedCustomer && selectedCustomer.id === customer.rowid,
                                          colorMap.current,
                                      ),
                                  )
                                : null}
                        </div>
                    </Stack>
                    <Button
                        className="NextButton"
                        data-testid="select-tenant-next-btn"
                        onClick={handleNextClick}
                        disabled={!selectedCustomer}
                    >
                        {t('customers.next')}
                    </Button>
                </Stack>
            ) : null}
            {tenantsList && tenantsList.length > 0 ? <Stack className="SIBackground" /> : null}
            <AddNewCustomerDialog
                progress={props.progress}
                addNewCustomer={addNewCustomer}
                open={openNewCustomer}
                onClose={() => setOpenNewCustomer(false)}
            />
        </Stack>
    );
};

export default Customers;
